import React, { useRef, useState } from 'react';
import { Turnstile } from '@marsidev/react-turnstile';
import classes from './ContactModal.module.scss';
import { API_ENDPOINTS } from '../config/api';

// Same gate as Signup/Login: /api/contact verifies a Turnstile token
// now that an enquiry lands in the sales pipeline rather than only an
// inbox. Rendered only when a site key is in the build, and the server
// skips verification in development when no keys are configured, so a
// local run needs no Cloudflare setup.
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || '';

interface ContactModalProps {
  onClose: () => void;
  userEmail: string;
  userName: string;
  subject?: string;
  title?: string;
  // Optional intro line rendered above the form — a one-liner that sets
  // the visitor's expectation for what to write and what happens next.
  description?: string;
  // When true, only the Cancel button closes the modal — backdrop
  // clicks are ignored and the ✕ button is hidden. Guards against
  // accidental dismissal on high-intent CTAs like Landing's "Let's talk"
  // where the user has typed into the form and a misclick on the
  // backdrop shouldn't blow the entry away.
  dismissOnlyOnCancel?: boolean;
  // Renders the building qualification fieldset (address, lots, services,
  // role) above the message box. Every proposal CTA sets this: those
  // enquiries become proposals, and a proposal can't be costed without
  // lots x services. Every field is optional, so general enquiry forms
  // elsewhere in the app are unchanged when it's left off.
  captureBuilding?: boolean;
}

// Matches the three service cards on the homepage, plus maintenance and
// the software on its own. Maintenance came off on 2026-10-18 with the
// shift to the three foundational services and went back on when
// /maintenance returned on 2026-10-25: it is not sold from the homepage
// cards or the locations pages, but it is a thing people ask us to quote.
// Compliance is still off - it comes with the app rather than on its own.
const SERVICE_OPTIONS = ['Cleaning', 'Caretaking / Building management', 'Grounds & gardens', 'Maintenance', 'Platform only'];
const ROLE_OPTIONS = ['Owner', 'Committee member', 'Strata manager', 'Tenant / resident', 'Other'];

const ContactModal: React.FC<ContactModalProps> = ({ onClose, userEmail, userName, subject, title = 'Get in touch', description, dismissOnlyOnCancel = false, captureBuilding = false }) => {
  const [name, setName] = useState(userName || '');
  const [email, setEmail] = useState(userEmail || '');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [buildingAddress, setBuildingAddress] = useState('');
  const [lotCount, setLotCount] = useState('');
  const [services, setServices] = useState<string[]>([]);
  const [enquirerRole, setEnquirerRole] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');
  const turnstileRef = useRef<any>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim() || !email.trim() || !phone.trim() || !message.trim()) {
      setError('Please fill in all required fields');
      return;
    }

    if (TURNSTILE_SITE_KEY && !turnstileToken) {
      setError('Please complete the verification below');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const response = await fetch(API_ENDPOINTS.contact, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim(),
          phone: phone.trim(),
          message: message.trim(),
          userName: name.trim(),
          subject,
          turnstileToken,
          ...(captureBuilding ? {
            buildingAddress: buildingAddress.trim(),
            lotCount,
            services,
            enquirerRole,
          } : {}),
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to send message');
      }

      setSuccess(true);
      setTimeout(() => { onClose(); }, 2000);
    } catch (err: any) {
      setError(err.message || 'Failed to send message. Please try again.');
      // The token is single-use, so a retry needs a fresh one.
      turnstileRef.current?.reset();
      setTurnstileToken('');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (dismissOnlyOnCancel) return;
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div className={classes.backdrop} onClick={handleBackdropClick}>
      <div className={classes.modal}>
        <div className={classes.header}>
          <div>
            <h2>{title}</h2>
            {subject && <p className={classes.subjectTag}>{subject}</p>}
          </div>
          {!dismissOnlyOnCancel && (
            <button type="button" className={classes.closeButton} onClick={onClose} aria-label="Close">✕</button>
          )}
        </div>

        {description && !success && (
          <p className={classes.description}>{description}</p>
        )}

        {success ? (
          <div className={classes.success}>
            <p>✓ Message sent successfully!</p>
            <p>We'll get back to you soon.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className={classes.form}>
            <div className={classes.formGroup}>
              <label htmlFor="contact-name">Name <span className={classes.required}>*</span></label>
              <input
                id="contact-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Jane Smith"
                required
                disabled={isSubmitting}
                autoComplete="name"
                className={classes.input}
              />
            </div>

            <div className={classes.formRow}>
              <div className={classes.formGroup}>
                <label htmlFor="contact-email">Email <span className={classes.required}>*</span></label>
                <input
                  id="contact-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  disabled={isSubmitting}
                  className={classes.input}
                />
              </div>
              <div className={classes.formGroup}>
                <label htmlFor="contact-phone">Phone <span className={classes.required}>*</span></label>
                <input
                  id="contact-phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+61 / +27 ..."
                  required
                  disabled={isSubmitting}
                  className={classes.input}
                />
              </div>
            </div>

            {captureBuilding && (
              <>
                <div className={classes.formGroup}>
                  <label htmlFor="contact-building">Building address</label>
                  <input
                    id="contact-building"
                    type="text"
                    value={buildingAddress}
                    onChange={(e) => setBuildingAddress(e.target.value)}
                    placeholder="12 Example St, Terrigal NSW"
                    disabled={isSubmitting}
                    autoComplete="street-address"
                    className={classes.input}
                  />
                </div>

                <div className={classes.formRow}>
                  <div className={classes.formGroup}>
                    <label htmlFor="contact-lots">Number of lots</label>
                    <input
                      id="contact-lots"
                      type="number"
                      min={0}
                      value={lotCount}
                      onChange={(e) => setLotCount(e.target.value)}
                      placeholder="141"
                      disabled={isSubmitting}
                      className={classes.input}
                    />
                  </div>
                  <div className={classes.formGroup}>
                    <label htmlFor="contact-role">Your role</label>
                    <select
                      id="contact-role"
                      value={enquirerRole}
                      onChange={(e) => setEnquirerRole(e.target.value)}
                      disabled={isSubmitting}
                      className={classes.input}
                    >
                      <option value="">Select...</option>
                      {ROLE_OPTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </div>
                </div>

                <fieldset className={classes.formGroup}>
                  <legend className={classes.legend}>Services you want</legend>
                  <div className={classes.checkRow}>
                    {SERVICE_OPTIONS.map((svc) => (
                      <label key={svc} className={classes.checkLabel}>
                        <input
                          type="checkbox"
                          checked={services.includes(svc)}
                          disabled={isSubmitting}
                          onChange={(e) => setServices((prev) => (
                            e.target.checked ? [...prev, svc] : prev.filter((s) => s !== svc)
                          ))}
                        />
                        {svc}
                      </label>
                    ))}
                  </div>
                </fieldset>
              </>
            )}

            <div className={classes.formGroup}>
              <label htmlFor="contact-message">Message <span className={classes.required}>*</span></label>
              <textarea
                id="contact-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="How can we help you today?"
                rows={3}
                disabled={isSubmitting}
                className={classes.textarea}
              />
            </div>

            {TURNSTILE_SITE_KEY && (
              <div className={classes.turnstile}>
                <Turnstile
                  ref={turnstileRef}
                  siteKey={TURNSTILE_SITE_KEY}
                  onSuccess={token => setTurnstileToken(token)}
                  onError={() => {
                    setTurnstileToken('');
                    setTimeout(() => turnstileRef.current?.reset(), 100);
                  }}
                  onExpire={() => {
                    setTurnstileToken('');
                    setTimeout(() => turnstileRef.current?.reset(), 100);
                  }}
                  options={{ size: 'flexible' }}
                />
              </div>
            )}

            {error && <div className={classes.error}>{error}</div>}

            <div className={classes.footer}>
              <button type="button" onClick={onClose} className={classes.cancelButton} disabled={isSubmitting}>
                Cancel
              </button>
              <button type="submit" className={classes.submitButton} disabled={isSubmitting || !name.trim() || !email.trim() || !phone.trim() || !message.trim() || (!!TURNSTILE_SITE_KEY && !turnstileToken)}>
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ContactModal;
